import { enrichedRelationSchema, } from '../interfaces/enrichedModuleSchema.js';
import { camelCaseToLabel, camelToSnakeCase } from '../utils/strings.js';
import { EnrichedColumn } from './EnrichedColumn.js';
import { assertBelongsToRelation } from '../utils/relations.js';
export class EnrichedRelation {
    parentModel;
    relationType;
    model;
    inverse;
    name;
    displayName;
    constructor(params, parentModel) {
        this.parentModel = parentModel;
        this.relationType = params.relationType;
        this.model = params.model;
        this.inverse = params.inverse;
        this.name = params.name;
        this.displayName = params.displayName;
    }
    /**
     * Creates an EnrichedRelation instance directly from a JSON string.
     *
     * This method parses the provided JSON string, which should represent an enriched relation object,
     * and constructs a new EnrichedRelation instance using the parsed data. Note that this method
     * does not perform schema validation; it assumes the JSON is well-formed and matches the
     * expected structure.
     *
     * @param json - A JSON string representing an enriched relation object.
     * @returns An EnrichedRelation instance populated with the parsed data.
     * @throws If the JSON is malformed or cannot be parsed.
     *
     * @example
     * ```typescript
     * const json = '{"relationType":"HasMany","model":"Comment","inverse":"post","name":"comments","displayName":"Comments"}'
     * const relation = EnrichedRelation.fromJSON(json)
     * ```
     */
    static fromJSON(json) {
        return new EnrichedRelation(JSON.parse(json));
    }
    /**
     * Safely creates an EnrichedRelation instance from a JSON string.
     *
     * This method parses the provided JSON string and validates its structure against the
     * enriched relation schema. If the JSON is invalid or does not conform to the expected
     * schema, an error is thrown. This provides type safety and ensures data integrity.
     *
     * @param json - A JSON string representing an enriched relation object.
     * @returns An EnrichedRelation instance populated with validated data.
     * @throws If the JSON is malformed or does not match the schema.
     *
     * @example
     * ```typescript
     * const json = '{"relationType":"HasMany","model":"Comment","inverse":"post","name":"comments","displayName":"Comments"}'
     * const relation = EnrichedRelation.fromJSONSafe(json)
     * ```
     */
    static fromJSONSafe(json) {
        return new EnrichedRelation(enrichedRelationSchema.parse(JSON.parse(json)));
    }
    /**
     * Converts the EnrichedRelation instance into a plain JavaScript object (POJO).
     *
     * This method transforms the EnrichedRelation class instance into a plain object
     * that contains all the relation properties. This is useful for serialization,
     * transport, or storage, as it strips away class methods and returns only the data structure.
     *
     * @returns A plain object representing the enriched relation with all its data.
     *
     * @example
     * ```typescript
     * const relation = new EnrichedRelation({...})
     * const pojo = relation.toPOJO()
     * // Returns: { relationType: 'HasMany', model: 'Comment', inverse: 'post', name: 'comments', displayName: 'Comments' }
     * ```
     */
    toPOJO() {
        return {
            relationType: this.relationType,
            model: this.model,
            inverse: this.inverse,
            name: this.name,
            displayName: this.displayName,
        };
    }
    /**
     * Serializes the EnrichedRelation instance to a JSON string.
     *
     * This method converts the EnrichedRelation instance to a plain object and then
     * stringifies it to JSON format. This is useful for sending relation data over
     * networks, storing in databases, or saving to files.
     *
     * @returns A JSON string representation of the enriched relation.
     *
     * @example
     * ```typescript
     * const relation = new EnrichedRelation({...})
     * const json = relation.toJSON()
     * // Returns: '{"relationType":"HasMany","model":"Comment","inverse":"post","name":"comments","displayName":"Comments"}'
     * ```
     */
    toJSON() {
        return JSON.stringify(this.toPOJO());
    }
    /**
     * Converts a camelCase relation name into a human-readable display name.
     *
     * This static utility method transforms camelCase naming conventions
     * (e.g., "userComments") into properly formatted display names (e.g., "User Comments")
     * suitable for user interfaces.
     *
     * @param name - The camelCase relation name to convert.
     * @returns A formatted display name suitable for UI display.
     *
     * @example
     * ```typescript
     * const displayName = EnrichedRelation.nameToDisplayName('userComments')
     * // Returns: 'User Comments'
     * ```
     */
    static nameToDisplayName(name) {
        return camelCaseToLabel(name);
    }
    /**
     * Sets the relation name and automatically generates formatted display names.
     *
     * This method updates the relation's name property and automatically generates
     * the corresponding displayName property using the static name conversion method.
     * It also updates the inverse relation on the related module to maintain consistency.
     *
     * @param name - The camelCase relation name to set.
     * @param registry - The module registry to look up related modules (defaults to this.moduleRegistry).
     * @throws ModuleNotInRegistryError if the related module is not found in the registry.
     * @throws InverseRelationDoesntExistError if the inverse relation doesn't exist on the related module.
     *
     * @example
     * ```typescript
     * const relation = new EnrichedRelation({...})
     * relation.setNames('userComments')
     * // Results in:
     * // relation.name = 'userComments'
     * // relation.displayName = 'User Comments'
     * // Updates the inverse relation on the related module
     * ```
     */
    // TODO: We should support name in any case style
    setNames(name) {
        this.name = name;
        this.displayName = EnrichedRelation.nameToDisplayName(name);
        const relatedModule = this.parentModel?.parentModule?.parentApp?.moduleRegistry.get(this.model);
        if (!relatedModule) {
            throw new ModuleNotInRegistryError('The related module is not in provided registry', this.model);
        }
        if (!relatedModule.model.relations[this.inverse]) {
            throw new InverseRelationDoesntExistError("The inverse relation doesn't exist on the related module", this.inverse);
        }
        relatedModule.model.relations[this.inverse].inverse = name;
    }
    /**
     * Creates a foreign key column for a BelongsTo relation on this model.
     *
     * This method generates an EnrichedColumn instance that represents a foreign key
     * column for a BelongsTo relation. The column name is automatically generated
     * from the related model name in snake_case format with '_id' suffix.
     *
     * @param order - The order position for the column (defaults to 0).
     * @returns An EnrichedColumn instance representing the foreign key column.
     * @throws If the relation is not a BelongsTo relation type.
     *
     * @example
     * ```typescript
     * const relation = new EnrichedRelation({
     *   relationType: 'BelongsTo',
     *   model: 'User',
     *   inverse: 'posts',
     *   name: 'author',
     *   displayName: 'Author'
     * })
     * const foreignKeyColumn = relation.makeForeignKeyForBelongsToRelation(1)
     * // Returns: EnrichedColumn with name 'user_id', type 'foreignId', forRelation 'author'
     * ```
     */
    makeForeignKeyForBelongsToRelation(order = Object.keys(this.parentModel?.columns ?? []).length) {
        const name = camelToSnakeCase(this.name) + '_id';
        assertBelongsToRelation(this);
        return new EnrichedColumn({
            name,
            displayName: EnrichedColumn.nameToDisplayName(name),
            displayNamePlural: EnrichedColumn.nameToDisplayNamePlural(name),
            type: 'foreignId',
            typeConstraints: [
                {
                    name: 'nullable',
                    type: 'modifier',
                    params: [],
                },
            ],
            forRelation: this.name,
            order,
        });
    }
}
export class ModuleNotInRegistryError extends Error {
    name;
    constructor(message, name) {
        super(message);
        this.name = name;
        this.name = 'ModuleNotInRegistryError';
    }
}
export class InverseRelationDoesntExistError extends Error {
    name;
    constructor(message, name) {
        super(message);
        this.name = name;
        this.name = 'InverseRelationDoesntExistError';
    }
}
